import { useState } from "react";
import { StudentLayout } from "@/components/Layouts";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

export default function StudentProfile() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/profile", { name, phone });
      await refreshUser();
      setEditing(false);
      toast.success("Profile updated.");
    } catch {
      toast.error("Gagal kemas kini profil.");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <StudentLayout title="Profile">
      <div className="mb-5">
        <h1 className="text-3xl font-heading font-bold tracking-tight">Profile</h1>
        <p className="mt-2 text-muted-foreground">Your student account details.</p>
      </div>

      <Card className="glass-card max-w-2xl" data-testid="profile-card">
        <CardContent className="p-6 sm:p-8 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name">
              {editing ? (
                <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" />
              ) : (
                <p className="font-medium">{user.name}</p>
              )}
            </Field>
            <Field label="Email">
              <p className="font-medium">{user.email}</p>
            </Field>
            <Field label="Phone">
              {editing ? (
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="profile-phone" />
              ) : (
                <p className="font-medium">{user.phone || "—"}</p>
              )}
            </Field>
            <Field label="Batch">
              <p className="font-medium">{user.batch || "—"}</p>
            </Field>
            <Field label="Course">
              <p className="font-medium">{user.course || "—"}</p>
            </Field>
            <Field label="Access Status">
              <StatusBadge status={user.access_status} />
            </Field>
            <Field label="Access Expiry">
              <p className="font-medium">{user.access_expiry || "—"}</p>
            </Field>
          </div>
          <div className="flex gap-2 pt-2">
            {editing ? (
              <>
                <Button onClick={save} disabled={saving} data-testid="profile-save">{saving ? "Saving..." : "Save"}</Button>
                <Button variant="outline" onClick={() => { setEditing(false); setName(user.name); setPhone(user.phone || ""); }}>Cancel</Button>
              </>
            ) : (
              <Button onClick={() => setEditing(true)} data-testid="profile-edit-btn">Edit Profile</Button>
            )}
          </div>
        </CardContent>
      </Card>
    </StudentLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground uppercase tracking-wider">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
